'use client';

import { useCallback, useEffect, useState } from 'react';
import {
  EVAL_POOL_FREE_SIZE,
  emptyEntitlements,
  type PlanLimits,
  type ResolvedEntitlements,
} from '@roomtreatment/core';
import { setEvalPoolSnapshot, subscribeEvalPool } from '@/lib/evalPoolState';
import { parseDevPlan, type DevPlan } from '@/lib/devAccount';

export interface WalletState {
  hasSubscription: boolean;
  isFounder: boolean;
  consultationUrl: string | null;
  /** null = unlimited compute credits. */
  evalPoolRemaining: number | null;
  evalPoolTotal: number;
  limits: PlanLimits | null;
  resolvedEntitlements: ResolvedEntitlements;
  authenticated: boolean;
  devTools: boolean;
  devPlan: DevPlan | null;
  loading: boolean;
}

type WalletSnapshot = Omit<WalletState, 'loading'>;

let cache: WalletSnapshot | null = null;
const subscribers = new Set<(s: WalletSnapshot) => void>();
let inflight: Promise<void> | null = null;

let cachedPlansEvalPoolTotal: number | null = null;
let plansInflight: Promise<number> | null = null;

function parseResolvedEntitlements(
  raw: unknown,
  fallbackTotal: number,
): ResolvedEntitlements {
  if (typeof raw !== 'object' || raw === null) {
    return emptyEntitlements(fallbackTotal);
  }
  const r = raw as Partial<ResolvedEntitlements>;
  return {
    compute_remaining: r.compute_remaining === null
      ? null
      : typeof r.compute_remaining === 'number' ? r.compute_remaining : fallbackTotal,
    compute_total: typeof r.compute_total === 'number' ? r.compute_total : fallbackTotal,
    max_rooms: typeof r.max_rooms === 'number' ? r.max_rooms : 1,
    features: Array.isArray(r.features) ? r.features.filter((f): f is ResolvedEntitlements['features'][number] => typeof f === 'string') : [],
    has_active_subscription: r.has_active_subscription === true,
    legacy_unlimited: r.legacy_unlimited === true,
  };
}

async function fetchPlansEvalPoolTotal(): Promise<number> {
  if (cachedPlansEvalPoolTotal != null) return cachedPlansEvalPoolTotal;
  if (plansInflight) return plansInflight;
  plansInflight = (async (): Promise<number> => {
    try {
      const res = await fetch('/api/billing/plans', { credentials: 'include', cache: 'no-store' });
      if (res.ok) {
        const d = await res.json().catch(() => ({}));
        if (typeof d.eval_pool_total === 'number') {
          cachedPlansEvalPoolTotal = d.eval_pool_total;
          return d.eval_pool_total;
        }
      }
    } catch {
      // fall through
    }
    return EVAL_POOL_FREE_SIZE;
  })();
  try {
    return await plansInflight;
  } finally {
    plansInflight = null;
  }
}

function emptyWalletSnapshot(evalPoolTotal: number): WalletSnapshot {
  return {
    hasSubscription: false,
    isFounder: false,
    consultationUrl: null,
    evalPoolRemaining: evalPoolTotal,
    evalPoolTotal,
    limits: { max_rooms: 1, max_designs_per_room: 1 },
    resolvedEntitlements: emptyEntitlements(evalPoolTotal),
    authenticated: false,
    devTools: false,
    devPlan: null,
  };
}

async function load(force = false): Promise<void> {
  if (inflight && !force) return inflight;
  inflight = (async () => {
    const plansEvalPoolTotal = await fetchPlansEvalPoolTotal();
    try {
      const res = await fetch('/api/auth/me', { credentials: 'include', cache: 'no-store' });
      if (!res.ok) {
        cache = emptyWalletSnapshot(plansEvalPoolTotal);
      } else {
        const d = await res.json().catch(() => ({}));
        const evalPoolRemaining =
          d.eval_pool_remaining === null ? null
          : typeof d.eval_pool_remaining === 'number' ? d.eval_pool_remaining
          : 0;
        const evalPoolTotal =
          typeof d.eval_pool_total === 'number' ? d.eval_pool_total : plansEvalPoolTotal;
        const isFounder = d.is_founder === true;
        const resolved = parseResolvedEntitlements(d.resolved_entitlements, evalPoolTotal);
        cache = {
          hasSubscription: !!d.has_subscription,
          isFounder,
          consultationUrl: isFounder && typeof d.consultation_url === 'string' ? d.consultation_url : null,
          evalPoolRemaining,
          evalPoolTotal,
          limits: d.limits ?? null,
          resolvedEntitlements: resolved,
          authenticated: true,
          devTools: d.dev_tools === true,
          devPlan: parseDevPlan(d.dev_plan),
        };
        setEvalPoolSnapshot({ remaining: evalPoolRemaining, total: evalPoolTotal });
      }
    } catch {
      cache = emptyWalletSnapshot(plansEvalPoolTotal);
    }
    subscribers.forEach((fn) => fn(cache!));
  })();
  try {
    await inflight;
  } finally {
    inflight = null;
  }
}

subscribeEvalPool((s) => {
  if (!cache || s.lastSpendAt == null) return;
  if (cache.evalPoolRemaining === s.remaining && cache.evalPoolTotal === s.total) return;
  cache = {
    ...cache,
    evalPoolRemaining: s.remaining,
    evalPoolTotal: s.total,
    resolvedEntitlements: {
      ...cache.resolvedEntitlements,
      compute_remaining: s.remaining,
      compute_total: s.total,
    },
  };
  subscribers.forEach((fn) => fn(cache!));
});

export function useWallet(): WalletState & { refresh: () => Promise<void> } {
  const [state, setState] = useState<WalletState>(
    cache
      ? { ...cache, loading: false }
      : { ...emptyWalletSnapshot(EVAL_POOL_FREE_SIZE), loading: true },
  );

  useEffect(() => {
    const onUpdate = (s: WalletSnapshot) => setState({ ...s, loading: false });
    subscribers.add(onUpdate);
    if (cache) onUpdate(cache);
    else void load();
    return () => {
      subscribers.delete(onUpdate);
    };
  }, []);

  const refresh = useCallback(() => load(true), []);
  return { ...state, refresh };
}
